import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import AuthLayout, { formatApiError } from "@/components/AuthLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnTo = params.get("returnTo") || "/grove";
  const { loginWithPassword, login: googleLogin } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await loginWithPassword(email.trim(), password);
      toast.success("Welcome back.");
      navigate(returnTo);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title="Welcome back."
      subtitle="Sign in to plant seeds, leave Field Notes, and keep your gift lists close."
      footer={
        <>
          New here?{" "}
          <Link to="/signup" data-testid="login-to-signup-link" className="text-[#4E5A3D] underline underline-offset-2">
            Create an account
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
        <div>
          <label className="text-xs uppercase tracking-wider text-[#2A2823]/60" htmlFor="login-email">Email</label>
          <input
            id="login-email"
            data-testid="login-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            className="mt-1 w-full rounded-full border border-[#B79256]/40 bg-white px-4 py-2.5 text-[15px] focus:outline-none focus:ring-2 focus:ring-[#4E5A3D]/30"
            required
          />
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label className="text-xs uppercase tracking-wider text-[#2A2823]/60" htmlFor="login-password">Password</label>
            <Link
              to="/forgot-password"
              data-testid="login-forgot-link"
              className="text-xs text-[#4E5A3D] underline underline-offset-2 hover:text-[#3E4830]"
            >
              Forgot?
            </Link>
          </div>
          <div className="mt-1 relative">
            <input
              id="login-password"
              data-testid="login-password-input"
              type={showPw ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="your password"
              autoComplete="current-password"
              className="w-full rounded-full border border-[#B79256]/40 bg-white px-4 py-2.5 pr-11 text-[15px] focus:outline-none focus:ring-2 focus:ring-[#4E5A3D]/30"
              required
            />
            <button
              type="button"
              data-testid="login-toggle-password"
              onClick={() => setShowPw((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#2A2823]/50 hover:text-[#2A2823]"
              aria-label={showPw ? "Hide password" : "Show password"}
            >
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {error && (
          <div
            data-testid="login-error"
            className="text-sm text-[#8a3a2a] bg-[#8a3a2a]/10 border border-[#8a3a2a]/25 rounded-2xl px-4 py-2.5"
          >
            {error}
          </div>
        )}

        <Button
          type="submit"
          data-testid="login-submit"
          disabled={submitting || !email || !password}
          className="w-full rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] h-12 text-base shadow-none disabled:opacity-60"
        >
          {submitting ? "Signing you in…" : "Sign in"}
        </Button>
      </form>

      <div className="my-6 flex items-center gap-3 text-xs text-[#2A2823]/50">
        <div className="flex-1 h-px bg-[#B79256]/25" />
        <span>or</span>
        <div className="flex-1 h-px bg-[#B79256]/25" />
      </div>

      <Button
        type="button"
        onClick={googleLogin}
        data-testid="login-google"
        variant="outline"
        className="w-full rounded-full h-12 border-[#B79256]/60 text-[#2A2823] hover:bg-[#B79256]/10 hover:text-[#2A2823] bg-transparent"
      >
        Continue with Google
      </Button>
    </AuthLayout>
  );
}
